/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ClinicPetApi from "../../../../api/ClinicPetApi";
import Dropdown from "../../../../components/Dropdown/Dropdown";
import LoaderInline from "../../../../components/LoaderInline";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal/Modal";
import Field from "../../../../components/ReactHookFormFields/General/Field";
import { Cross } from "../../../../common/Icons/Cross";
import { strings } from "../../../../common/Strings/Strings";
import logger from "../../../../util/logger";
import {
  getValueOfActiveUser,
  LoggedInUser,
  PetVetDataCredentials,
  updateLoggedInUser,
} from "../../../../util/LocalStorageVariables";
import { PetVetApiResponse } from "../../../../models/pet/PetVetDataResponse";
import AlertBox from "../../../../components/AlertBox";
import SearchInputWithSearchButton from "../../../../components/InputFieldsSimple/SearchInputWithSearchButton";
import Tag, { TagType } from "../../../../components/Tag";
import { getGeneralError } from "../../../../util/helperFunctions";

interface Props {
  onSelectChipData(data: PetVetApiResponse | null): void;
  previouslySelectedResult?: PetVetApiResponse;
}

const ChipSearch = ({ onSelectChipData, previouslySelectedResult }: Props): ReactElement => {
  const [chipNumber, setChipNumber] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isDown, setIsDown] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<PetVetApiResponse | null>(null);
  const [selectedPetVetDataResult, setSelectedPetVetDataResult] = useState<PetVetApiResponse | null>(null);
  const [isCredentialModalOpen, setIsCredentialsModalOpen] = useState<boolean>(false);
  const [isPreviousResult, setIsPreviousResult] = useState<boolean>(false);
  const [error, setError] = useState<string | null>();

  const { getValues, register, handleSubmit } = useForm<PetVetDataCredentials>();

  useEffect(() => {
    if (previouslySelectedResult) {
      setSelectedPetVetDataResult(previouslySelectedResult);
      setSearchResult(previouslySelectedResult);
      setIsPreviousResult(true);
    }
  }, [previouslySelectedResult]);

  const searchPet = async (chip: string, isNewCredentials?: boolean) => {
    setError(null);   
    setIsCredentialsModalOpen(false);
    const storedData = getValueOfActiveUser("petvetdata");
    let isCredentialsNew = false;
    let username = storedData?.username;
    let password = storedData?.password;

    if (!username || !password || isNewCredentials) {
      username = getValues("username");
      password = getValues("password");
      isCredentialsNew = true;
    }
    if (username && password) {
      try {
        setLoading(true);
        const response = await ClinicPetApi.searchPetByChipNumber(chip, {
          username,
          password,
          isCredentialsNew,
        });
        setSearchResult(response.data.api);
        setIsPreviousResult(false);

        if (response.data.error) {
          setError(response.data.error);
        } else if (response.data.api.result === "error") {
          setError(response.data.api.data?.error);
        }

        const encryptedUsername = response.data.encryptedUsername;
        const encryptedPassword = response.data.encryptedPassword;
        setIsDown(true);

        updateLoggedInUser((u: LoggedInUser) => ({
          ...u,
          petvetdata: {
            username: encryptedUsername,
            password: encryptedPassword,
          },
        }));
      } catch (e) {
        logger.error(e);
        setSearchResult(null);
        setError(await getGeneralError(e));
      } finally {
        setLoading(false);
      }
    } else {
      setIsCredentialsModalOpen(true);
    }
  };

  const onSubmitCredentials = () => {
    searchPet(chipNumber, true);
  };

  return (
    <>
      {selectedPetVetDataResult ? (
        <div className="tw-card px-4 py-3 flex items-center">
          <div>
            <Tag text="PetVetData" type={TagType.primary} />
          </div>
          <div className="ml-auto">
            <Button
              onClick={() => {
                setSelectedPetVetDataResult(null);
                onSelectChipData(null);
                setChipNumber("");
              }}
              variant="icon"
            >
              <Cross />
            </Button>
          </div>
        </div>
      ) : (
        <Dropdown isOpen={isDown} className="w-full">
          <Dropdown.Toggle>
            <div className="relative w-full">
              <SearchInputWithSearchButton
                label={strings.searchChipLabel}
                loading={loading}
                onChange={setChipNumber}
                onEnterDown={() => {
                  if (chipNumber.trim()) {
                    searchPet(chipNumber.trim());
                  }
                }}
                onSearch={() => {
                  if (chipNumber.trim()) {
                    searchPet(chipNumber.trim());
                  }
                }}
                placeholder={strings.chipNumber}
                readOnly={loading}
                value={chipNumber}
              />
            </div>
          </Dropdown.Toggle>
          <Dropdown.Content>
            <div className="text-sm">
              {loading ? (
                <div className="px-4 py-3">
                  <LoaderInline />
                </div>
              ) : (
                <div className="px-4 py-3">
                  {error ? (
                    <div className="space-y-3">
                      <AlertBox
                        closeAble={false}
                        message={
                          <>
                            {error}
                            {error === "Nincs joga a művelet végrehajtásához." && (
                              <Button className="text-red-700" onClick={() => setIsCredentialsModalOpen(true)} variant="link">
                                {strings.checkCredentialsButton}
                              </Button>
                            )}
                          </>
                        }
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                  {!!searchResult?.data && searchResult?.result === "success" ? (
                    <>
                      {isPreviousResult && <div>{strings.previousMatch}</div>}
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          onSelectChipData?.(searchResult);
                          setSelectedPetVetDataResult(searchResult);
                          setIsDown(false);
                          setIsPreviousResult(true);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="flex flex-wrap items-center space-x-2">
                          <div className="font-semibold">{searchResult?.data?.allat_nev}</div>
                          <div>
                            ({searchResult?.data?.allat_faj}, {searchResult?.data?.allat_fajta},{" "}
                            {searchResult?.data?.allat_ivar}, {searchResult?.data?.allat_chip_szam})
                          </div>
                        </div>
                        <div className="flex">
                          <div>{searchResult?.data?.tul_nev}</div>
                          {searchResult?.data?.tul_mobil && <div className="ml-2">{searchResult?.data?.tul_mobil}</div>}
                        </div>
                        <div>
                          {searchResult?.data?.tul_irszam} {searchResult?.data?.tul_varos},{" "}
                          {searchResult?.data?.tul_utca}
                        </div>
                      </div>
                    </>
                  ) : (
                    <></>
                  )}
                  {!searchResult && !error && <>{strings.enterChipNumberMessage}</>}
                </div>
              )}
            </div>
          </Dropdown.Content>
        </Dropdown>
      )}
      <Modal handleClose={() => setIsCredentialsModalOpen(false)} show={isCredentialModalOpen}>
        <Modal.Header title={strings.petVetDataCredentials} />
        <Modal.Body>
          <form id="credentialsForm" onSubmit={handleSubmit(onSubmitCredentials)}>
            <div className="space-y-4">
              <Field label={strings.usernameLabel} name="username" register={register} required type="text" />
              <Field label={strings.passwordLabel} name="password" register={register} required type="password" />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button className="modal-main-button" form="credentialsForm" type="submit">
            {strings.save}
          </Button>
          <Button className="modal-sub-button" onClick={() => setIsCredentialsModalOpen(false)} variant="secondary">
            {strings.cancel}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChipSearch;
